import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { ContentTagsField, LinkTargetSelect } from '@/components/shared';
import { FormField, FormHeader, FormSpacer, FormStickyActions } from '@/components/forms';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import { LanguageTabs } from '@/components/universal';
import {
  Settings,
  Image as ImageIcon,
  Monitor,
  Tablet,
  Smartphone,
  Link as LinkIcon,
} from 'lucide-react';
import {
  type Banner,
  type BannerFormData,
  type BannerImageInput,
  DEVICE_SLOTS,
} from './types';

// ============================================
// Types
// ============================================

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

interface LanguageTabItem {
  code: string;
  label: string;
  flagEmoji?: string;
}

/** Generic entity from any resource API */
/** Image slot key: "desktop", "tablet", "mobile" — optionally with language suffix */
interface ImageSlot {
  aspectRatio: string;
  deviceLabel: string;
  languageCode: string | null;
  imageId: number | null;
  imageUrl: string;
}

// ============================================
// Helpers
// ============================================

const DEVICE_ICONS = {
  Desktop: Monitor,
  Tablet: Tablet,
  Mobile: Smartphone,
} as const;

function buildImageSlots(
  existingImages: BannerImageInput[],
  languages: LanguageTabItem[] | undefined,
  localizationEnabled: boolean,
): ImageSlot[] {
  const slots: ImageSlot[] = [];

  // Мобильный слот сменился 16:9 → 16:7. Старые баннеры с картинкой 16:9
  // подхватываются в слот 16:7 (при сохранении пересохранятся уже как 16:7),
  // иначе при редактировании старого баннера мобильная картинка терялась бы.
  const LEGACY_MOBILE_FALLBACK: Record<string, string> = { '16:7': '16:9' };
  const findExisting = (deviceAspect: string, langCode: string | null) => {
    const byLang = (img: BannerImageInput) =>
      langCode ? img.languageCode === langCode : !img.languageCode;
    const exact = existingImages.find((img) => img.aspectRatio === deviceAspect && byLang(img));
    if (exact) { return exact; }
    const legacyAspect = LEGACY_MOBILE_FALLBACK[deviceAspect];
    return legacyAspect
      ? existingImages.find((img) => img.aspectRatio === legacyAspect && byLang(img))
      : undefined;
  };

  if (localizationEnabled && languages && languages.length > 0) {
    for (const lang of languages) {
      for (const device of DEVICE_SLOTS) {
        const existing = findExisting(device.aspectRatio, lang.code);
        slots.push({
          aspectRatio: device.aspectRatio,
          deviceLabel: device.label,
          languageCode: lang.code,
          imageId: existing?.imageId ?? null,
          imageUrl: existing?.imageUrl ?? '',
        });
      }
    }
  } else {
    for (const device of DEVICE_SLOTS) {
      const existing = findExisting(device.aspectRatio, null);
      slots.push({
        aspectRatio: device.aspectRatio,
        deviceLabel: device.label,
        languageCode: null,
        imageId: existing?.imageId ?? null,
        imageUrl: existing?.imageUrl ?? '',
      });
    }
  }

  return slots;
}

function slotsToImageInputs(slots: ImageSlot[]): BannerImageInput[] {
  return slots
    .filter((s): s is ImageSlot & { imageId: number } => s.imageId !== null)
    .map((s, i) => ({
      imageId: s.imageId,
      imageUrl: s.imageUrl,
      aspectRatio: s.aspectRatio,
      languageCode: s.languageCode,
      type: 'background' as const,
      order: i,
    }));
}

// ============================================
// Main Form
// ============================================

export function BannersForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<Banner>({
    resource: 'banners',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const languages: LanguageTabItem[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    label: l.nativeName || l.name,
    flagEmoji: l.flagEmoji || l.flag || undefined,
  }));

  const banner = query?.data?.data;
  const formKey = isEdit ? `edit-${banner?.id ?? 'loading'}` : 'create';

  return (
    <BannersFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      banner={banner}
      order={banner?.order ?? 0}
      onFinish={async (v) => {
        await onFinish(v);
      }}
      onBack={() => navigate(-1)}
      languages={languages}
    />
  );
}

// ============================================
// Form Fields
// ============================================

interface BannersFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  banner?: Banner;
  order: number;
  onFinish: (values: BannerFormData) => Promise<void>;
  onBack: () => void;
  languages?: LanguageTabItem[];
}

function BannersFormFields({
  isEdit,
  formLoading,
  banner,
  order,
  onFinish,
  onBack,
  languages,
}: BannersFormFieldsProps) {
  // Link state
  const [linkUrl, setLinkUrl] = useState<string | null>(banner?.linkUrl ?? null);

  // Dates & status
  const [startDate, setStartDate] = useState<Date | null>(
    banner?.startDate ? new Date(banner.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    banner?.endDate ? new Date(banner.endDate) : null,
  );
  const [isActive, setIsActive] = useState(banner?.isActive ?? true);

  // Localization
  const [localizationEnabled, setLocalizationEnabled] = useState(() => {
    const imgs = banner?.images ?? [];
    return imgs.some((img) => img.languageCode !== null);
  });
  const [activeLanguage, setActiveLanguage] = useState(languages?.[0]?.code ?? 'ru');

  // Image slots
  const existingImages: BannerImageInput[] = (banner?.images ?? []).map((img) => ({
    imageId: img.imageId,
    imageUrl: img.imageUrl,
    aspectRatio: img.aspectRatio,
    languageCode: img.languageCode,
    type: (img.type ?? 'background') as 'background' | 'logo',
    order: img.order,
  }));

  const [imageSlots, setImageSlots] = useState<ImageSlot[]>(() =>
    buildImageSlots(existingImages, languages, localizationEnabled),
  );

  const updateSlotImage = (index: number, fileRecord: { id: number; url: string } | null) => {
    setImageSlots((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, imageId: fileRecord?.id ?? null, imageUrl: fileRecord?.url ?? '' }
          : s,
      ),
    );
  };

  const handleLocalizationChange = (enabled: boolean) => {
    setLocalizationEnabled(enabled);
    const currentInputs = slotsToImageInputs(imageSlots);
    setImageSlots(buildImageSlots(currentInputs, languages, enabled));
  };

  // Preview image
  const _previewImage =
    imageSlots.find((s) => s.imageUrl && s.deviceLabel === 'Desktop')?.imageUrl ??
    imageSlots.find((s) => s.imageUrl)?.imageUrl ??
    '';

  const hasAnyImage = imageSlots.some((s) => s.imageId !== null);

  const handleSubmit = async () => {
    const formData: BannerFormData = {
      images: slotsToImageInputs(imageSlots),
      linkUrl,
      order,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      isActive,
    };
    await onFinish(formData);
  };

  // Filter slots for current language tab (or all if not localized)
  const visibleSlots = localizationEnabled
    ? imageSlots
        .map((slot, originalIndex) => ({ slot, originalIndex }))
        .filter(({ slot }) => slot.languageCode === activeLanguage)
    : imageSlots.map((slot, originalIndex) => ({ slot, originalIndex }));

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать баннер' : 'Создать баннер'}
        onBack={onBack}
        sticky
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left — Images */}
        <div className="xl:col-span-2 space-y-6">
          {/* Images Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Изображения
                </span>
                {languages && languages.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="localization-toggle" className="text-xs font-normal text-muted-foreground cursor-pointer">
                      Локализация
                    </Label>
                    <Select
                      value={localizationEnabled ? 'on' : 'off'}
                      onValueChange={(v) => handleLocalizationChange(v === 'on')}
                    >
                      <SelectTrigger id="localization-toggle" className="w-[100px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Выкл</SelectItem>
                        <SelectItem value="on">Вкл</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Language tabs if localization enabled */}
              {localizationEnabled && languages && languages.length > 0 && (
                <LanguageTabs
                  languages={languages}
                  activeLanguage={activeLanguage}
                  onLanguageChange={setActiveLanguage}
                />
              )}

              {/* Device slots */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {visibleSlots.map(({ slot, originalIndex }) => {
                  const DeviceIcon = DEVICE_ICONS[slot.deviceLabel as keyof typeof DEVICE_ICONS] ?? Monitor;
                  const deviceMeta = DEVICE_SLOTS.find((d) => d.label === slot.deviceLabel);

                  return (
                    <div key={originalIndex} className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <DeviceIcon className="h-3.5 w-3.5" />
                        <span className="font-medium">{slot.deviceLabel}</span>
                        <span>({deviceMeta?.description})</span>
                      </div>
                      <FileImageUpload
                        value={slot.imageId ? { id: slot.imageId, url: slot.imageUrl } : null}
                        onChange={(fileRecord) => updateSlotImage(originalIndex, fileRecord)}
                        placeholder={`${slot.aspectRatio}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
                    {/* Link Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Ссылка
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <LinkTargetSelect value={linkUrl} onChange={setLinkUrl} />
            </CardContent>
          </Card>
        </div>

        {/* Right — Settings & Link */}
        <div className="space-y-6">


          {/* Settings Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Настройки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Начало показа" name="startDate">
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Конец показа" name="endDate">
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Статус" name="isActive">
                <Select
                  value={isActive ? 'active' : 'inactive'}
                  onValueChange={(v) => setIsActive(v === 'active')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Активен</SelectItem>
                    <SelectItem value="inactive">Неактивен</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </CardContent>
          </Card>

          {isEdit && banner?.id ? (
            <Card>
              <CardContent className="pt-6">
                <ContentTagsField entityType="BANNER" entityId={banner.id} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button onClick={handleSubmit} disabled={formLoading || !hasAnyImage}>
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
        <Button variant="outline" onClick={onBack}>
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}
